import {
  DakenService,
  Environment,
  localForageHelper,
  LogService,
  onEnvironmentChange,
  Provider,
  ProxyAuthService,
  UserHelper,
  UTMS,
} from '@hub/shared'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { SubscriptionLike } from 'rxjs'
import useMessage from '../use-message/use-message.hook'

interface Props {
  email?: string
  password?: string
}

export default ({ email, password }: Props) => {
  const dakenService = useMemo(() => new DakenService(), [])
  const proxyAuthService = useMemo(() => new ProxyAuthService(), [])
  const onEnviromentChangeSub = useRef<SubscriptionLike | undefined>(undefined)
  const userHelper = useMemo(() => new UserHelper(), [])
  const logService = useMemo(() => new LogService(), [])
  const [environments, setEnvironments] = useState<Environment | undefined>(undefined)
  const [loading, setLoading] = useState(false)

  logService.init('useDoLogin init')

  const { showMessage } = useMessage()

  const connectToProvider = async (provider: Provider) => {
    const providerFactory: { [key: string]: () => void } = {
      google: () => proxyAuthService?.googleLogin(),
    }

    return providerFactory[provider]()
  }

  const resetLoginStorage = useCallback(() => {
    localForageHelper.removeItem('email')
    localForageHelper.removeItem('password')
  }, [])

  const toRegister = () => {
    const link = document.getElementById('link-to-register')
    if (link) {
      link.click()
    }
  }

  useEffect(() => {
    onEnviromentChangeSub.current = onEnvironmentChange.subscribe((environment: Environment) => {
      setEnvironments(environment)
    })

    return () => onEnviromentChangeSub?.current?.unsubscribe()
  }, [onEnviromentChangeSub])

  const sendEmail = useCallback(async () => {
    if (!loading) {
      setLoading(true)
      try {
        const thisEmail: string = (await localForageHelper.getItem('email')) || ''
        if (!thisEmail) {
          throw new Error('unexpected')
        }

        await dakenService.resendEmailAuth0(thisEmail)
        showMessage({ message: 'resendEmail' })
      } catch (error) {
        showMessage({ message: 'unexpected', type: 'error' })
        logService.error({
          message: 'Resend email error',
          error,
        })
      } finally {
        userHelper.deleteUser()
        resetLoginStorage()
      }
    }
    setLoading(false)
  }, [loading, logService, resetLoginStorage, showMessage, setLoading, dakenService, userHelper])

  const handlingErrors = useCallback(
    async (error = 'unexpected') => {
      let onClick
      try {
        if (error === 'mail_verified') {
          onClick = () => sendEmail()
        }
        if (error.startsWith('unregistered_user')) {
          onClick = () => toRegister()
        }
      } catch (error) {
        error = 'unexpected'
      } finally {
        if (error === 'Firebase: Error (auth/popup-closed-by-user).') {
          return
        }
        showMessage({ message: error, onClick, type: 'error' })
      }
    },
    [showMessage, sendEmail]
  )

  const removeUtms = useCallback(async () => {
    await localForageHelper.removeItem('utms')
  }, [])

  const saveUtms = useCallback(async () => {
    const params = new URLSearchParams(window.location.search)
    const utms = {
      utm_source: params.get('utm_source'),
      utm_medium: params.get('utm_medium'),
      utm_campaign: params.get('utm_campaign'),
      utm_term: params.get('utm_term'),
      utm_content: params.get('utm_content'),
      ad_id: params.get('ad_id'),
      ad_name: params.get('ad_name'),
    }

    if (Object.values(utms).some((utm) => utm)) {
      await localForageHelper.setItem('utms', utms)
    }
  }, [])

  const getUtms = useCallback(async (): Promise<UTMS> => {
    const utms = await localForageHelper.getItem('utms')
    return utms || {}
  }, [])

  const doLogin = useCallback(
    async (e: any = null): Promise<void> => {
      if (e) {
        e.preventDefault()
      }
      try {
        setLoading(true)
        await saveUtms()
        if (!loading) {
          localForageHelper.setItem('email', email)
          const encodeCredentials = window.btoa(`${email}|${password}`)
          window.location.assign(
            `${environments?.squidApis?.proxy}/firebase?data=${encodeCredentials}&squid-callback=${window?.location?.origin}/login`
          )
        }
      } catch (err: any) {
        handlingErrors(err?.message || err?.description || 'unexpected')
        setLoading(false)
        logService.error({
          message: 'Login error',
          error: err,
        })
      }
    },
    [loading, email, password, logService, setLoading, environments, handlingErrors, saveUtms]
  )

  const doLoginWithProvider = async (provider: Provider) => {
    try {
      setLoading(true)
      await saveUtms()
      await connectToProvider(provider)
    } catch (error: unknown) {
      const err: Error = error as Error
      handlingErrors(err?.message || 'unexpected')
    } finally {
      setLoading(false)
    }
  }

  return { doLogin, doLoginWithProvider, handlingErrors, resetLoginStorage, loading, setLoading, saveUtms, getUtms, removeUtms }
}
